import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * Project-aware `tailwind-merge` instance.
 *
 * Stock `tailwind-merge` only knows the utilities Tailwind ships out of the
 * box. This codebase adds a sizable set of custom design-system utilities in
 * `index.css`:
 *
 *   - **Custom font-size scale** — `text-eyebrow{,-sm,-xs}`, `text-body{,-sm,-lg,-default,-relaxed}`,
 *     `text-display-{sm,md,lg}`, `text-page-title{,-sm,-lg}`, `text-kpi{,-sm,-lg}`,
 *     `text-nav-item`. These all set font-size + line-height + (sometimes)
 *     letter-spacing / weight, so they MUST conflict-merge against each other
 *     and against Tailwind's stock `text-{xs..9xl}`.
 *
 *   - **Semantic color utilities** — `text-foreground`, `text-muted-foreground`,
 *     `text-card-foreground`, `text-popover-foreground`, `text-primary-foreground`,
 *     `text-secondary-foreground`, `text-accent-foreground`,
 *     `text-destructive-foreground`, `text-destructive-text`, `text-success-foreground`,
 *     `text-success-text`, `text-warning-text`, `text-info-text`. These set
 *     `color`, NOT font-size — they MUST live in a separate group from the
 *     custom font-size scale, otherwise stock tailwind-merge groups every
 *     `text-*` together and silently drops one when both appear in the same
 *     `cn()` call (the bug that made `<TableCell className="text-body-lg
 *     text-foreground">` render as just `text-foreground`).
 *
 * Without this extension `cn('text-body-lg', 'text-foreground')` collapses to
 * a single class — usually `text-foreground` — and the typography vanishes.
 * The extension below splits font-size and text-color into separate
 * `classGroups` so both survive the merge.
 *
 * The custom-color override applies to `bg-*` semantic utilities too
 * (`bg-success-muted`, `bg-destructive-muted`, etc.) for symmetry, since
 * stock tailwind-merge has the same blind spot there.
 *
 * Anywhere this codebase uses `cn(...)` (every component, every shadcn
 * primitive that re-exports it), this configured instance handles the merge.
 *
 * Spec: meridian-redesign — design-system color/typography token wiring.
 */
const customMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      // Font-size group: combine our custom utilities with the stock scale so
      // any two collide as expected.
      'font-size': [
        { text: [
          'eyebrow', 'eyebrow-sm', 'eyebrow-xs',
          'body', 'body-sm', 'body-lg', 'body-default', 'body-relaxed',
          'display-sm', 'display-md', 'display-lg',
          'page-title', 'page-title-sm', 'page-title-lg',
          'kpi', 'kpi-sm', 'kpi-lg',
          'nav-item',
        ] },
      ],
      // Text-color group: register every semantic text-color utility so it's
      // grouped separately from font-size and dedupes correctly against itself.
      'text-color': [
        { text: [
          'foreground',
          'muted-foreground',
          'card-foreground',
          'popover-foreground',
          'primary-foreground',
          'secondary-foreground',
          'accent-foreground',
          'destructive-foreground',
          'destructive-text',
          'success-foreground',
          'success-text',
          'warning-text',
          'info-text',
        ] },
      ],
      // Background-color group: same treatment for the semantic bg-* utilities
      // that don't ship with stock Tailwind.
      'bg-color': [
        { bg: [
          'foreground',
          'muted',
          'card',
          'popover',
          'primary',
          'secondary',
          'accent',
          'destructive',
          'destructive-muted',
          'success',
          'success-muted',
          'warning-muted',
          'info-muted',
        ] },
      ],
    },
  },
});

/**
 * Compose Tailwind class strings with conflict resolution.
 *
 * Combines `clsx` (conditional class composition) with the project-aware
 * `tailwind-merge` instance above so callers can pass strings, arrays, falsy
 * values, and conditional objects and receive a single deduplicated class
 * string that correctly distinguishes our custom design-system utilities.
 *
 * Requirement 4.2 — shadcn-style `cn()` utility.
 */
export function cn(...inputs: ClassValue[]): string {
  return customMerge(clsx(inputs));
}

/** Exposed for the cn() property test, which compares cn(...) to the merger
 *  it actually uses (not bare `twMerge`). */
export const __cnTwMerge = customMerge;
